import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, Shield, Calendar, ArrowRight } from "lucide-react";

export default function MaintenancePlans() {
  const prefersReducedMotion = useReducedMotion();

  const containerVariants = {
    hidden: { opacity: prefersReducedMotion ? 1 : 0 },
    visible: {
      opacity: 1,
      transition: prefersReducedMotion ? { duration: 0 } : {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: prefersReducedMotion ? 1 : 0, y: prefersReducedMotion ? 0 : 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: prefersReducedMotion ? { duration: 0 } : {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const handleContactClick = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="py-16 md:py-24 relative overflow-hidden" data-testid="section-maintenance">
      <div className="absolute inset-0 bg-gradient-to-r from-[#ffd700]/10 via-transparent to-[#ffd700]/10 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <motion.div
          initial={{ opacity: prefersReducedMotion ? 1 : 0, y: prefersReducedMotion ? 0 : -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold gradient-text mb-4 tracking-tight" data-testid="text-maintenance-title">
            Ongoing Property Maintenance Plans
          </h2>
          <p className="text-lg md:text-xl text-[#e0e0e0]/70 max-w-3xl mx-auto mb-4" data-testid="text-maintenance-subtitle">
            Beyond one-time projects, HHS Build can take your property under <span className="text-[#ffd700] font-semibold">regular, ongoing maintenance</span> so you don't have to think about repairs again.
          </p>
          <p className="text-base text-gray-400 max-w-2xl mx-auto">
            Quarterly or bi-annual home checkups, preventive maintenance, priority scheduling, and discounted handyman rates for maintenance clients.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 mb-12"
        >
          {/* Benefits List */}
          <motion.div variants={itemVariants} className="md:col-span-3">
            <Card className="glass-card p-8 hover-lift transition-all duration-500" data-testid="card-maintenance-benefits">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="bg-[#ffd700]/20 backdrop-blur-md p-2 rounded-lg border border-[#ffd700]/30">
                      <Calendar className="h-5 w-5 text-[#ffd700]" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-[#ffd700] font-semibold mb-1">Regular Checkups</h4>
                    <p className="text-sm text-gray-400">Quarterly or bi-annual visits</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="bg-[#ffd700]/20 backdrop-blur-md p-2 rounded-lg border border-[#ffd700]/30">
                      <Shield className="h-5 w-5 text-[#ffd700]" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-[#ffd700] font-semibold mb-1">Preventive Care</h4>
                    <p className="text-sm text-gray-400">Plumbing, caulking, grout, paint</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="bg-[#ffd700]/20 backdrop-blur-md p-2 rounded-lg border border-[#ffd700]/30">
                      <CheckCircle2 className="h-5 w-5 text-[#ffd700]" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-[#ffd700] font-semibold mb-1">Priority Service</h4>
                    <p className="text-sm text-gray-400">Faster scheduling & discounts</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="bg-[#ffd700]/20 backdrop-blur-md p-2 rounded-lg border border-[#ffd700]/30">
                      <Shield className="h-5 w-5 text-[#ffd700]" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-[#ffd700] font-semibold mb-1">1-Year Warranty</h4>
                    <p className="text-sm text-gray-400">On all maintenance work</p>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: prefersReducedMotion ? 1 : 0, y: prefersReducedMotion ? 0 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.4, duration: 0.6 }}
          className="text-center"
        >
          <p className="text-[#e0e0e0]/70 mb-6 text-lg">
            Ideal for busy families, out-of-state owners, and rental properties
          </p>
          <Button
            size="lg"
            className="text-lg px-10 py-7 h-auto font-semibold glass-surface hover:glow-gold hover-lift transition-all duration-300 border border-[#ffd700]/30"
            onClick={handleContactClick}
            data-testid="button-maintenance-cta"
          >
            <span className="text-[#ffd700]">Ask About Maintenance Plans</span>
            <ArrowRight className="ml-2 h-5 w-5 text-[#ffd700]" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
